import { useState, useEffect } from "react";
import { View, Text, TextInput, Pressable, ActivityIndicator } from "react-native";
import { useRouter, useLocalSearchParams } from "expo-router";
import { collection, doc, getDocs, getDoc, updateDoc, query, where } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { createTrainingSeries, deleteTrainingSeries } from "@/lib/services";
import { colors, radius } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { useAlert } from "@/hooks/useAlert";
import AlertSheet from "@/components/AlertSheet";
import { DateInput, TimeInput } from "@/components/DateTimeInputs";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function EditTrainingScreen() {
  const router = useRouter();
  const { user } = useAuth();
  const { club } = useClub();
  const { alertProps, alert } = useAlert();
  const params = useLocalSearchParams<{
    seriesId: string; title: string; time: string; duration: string;
    location: string; fee: string; dayOfWeek?: string; startDate?: string; endDate?: string;
  }>();

  const [title, setTitle] = useState(params.title || "");
  const [dayOfWeek, setDayOfWeek] = useState(params.dayOfWeek ? parseInt(params.dayOfWeek) : 2);
  const [time, setTime] = useState(params.time || "19:00");
  const [duration, setDuration] = useState(params.duration || "90");
  const [location, setLocation] = useState(params.location || "");
  const [startDate, setStartDate] = useState(params.startDate || "");
  const [endDate, setEndDate] = useState(params.endDate || "");
  const [fee, setFee] = useState(params.fee ? String(Number(params.fee) / 100) : "0");
  const [saving, setSaving] = useState(false);
  const [loadingSeries, setLoadingSeries] = useState(!params.dayOfWeek);

  // Fetch series data if not passed via params
  useEffect(() => {
    if (params.dayOfWeek || !club || !params.seriesId) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "clubs", club.id, "trainingSeries", params.seriesId));
        if (snap.exists()) {
          const s = snap.data() as any;
          setDayOfWeek(s.dayOfWeek ?? 2);
          setStartDate(s.startDate || "");
          setEndDate(s.endDate || "");
          if (!params.title && s.title) setTitle(s.title);
          if (!params.time && s.time) setTime(s.time);
          if (!params.duration && s.duration) setDuration(String(s.duration));
          if (!params.location && s.location) setLocation(s.location);
          if (!params.fee && s.fee != null) setFee(String(s.fee / 100));
        }
      } catch {} finally { setLoadingSeries(false); }
    })();
  }, [club?.id, params.seriesId]);

  const dayChanged = params.dayOfWeek && parseInt(params.dayOfWeek) !== dayOfWeek;
  const datesChanged = (params.startDate && params.startDate !== startDate) || (params.endDate && params.endDate !== endDate);
  const needsRecreate = dayChanged || datesChanged;

  const handleSave = async () => {
    if (!club || !user || !params.seriesId) return;
    if (!title.trim() || !location.trim()) {
      await alert({ title: "Missing fields", message: "Title and location are required." });
      return;
    }
    setSaving(true);
    try {
      const feeInPence = Math.round((parseFloat(fee) || 0) * 100);

      if (needsRecreate) {
        // Day or dates changed — delete future sessions and recreate
        if (!startDate || !endDate) {
          await alert({ title: "Missing dates", message: "Start and end dates are required when changing the day or dates." });
          setSaving(false);
          return;
        }
        await deleteTrainingSeries(club.id, params.seriesId, "future");
        await createTrainingSeries(club.id, {
          title: title.trim(),
          dayOfWeek,
          time,
          duration: parseInt(duration) || 90,
          startDate,
          endDate,
          location: location.trim(),
          fee: feeInPence,
          clubName: club.name,
          createdBy: user.uid,
          availabilityLeadDays: club.availabilityLeadDays ?? 7,
        });
        await alert({ title: "Updated", emoji: "✅", message: "Series recreated with new schedule." });
      } else {
        // Simple field update — no schedule change
        const updates = {
          title: title.trim(),
          time,
          duration: parseInt(duration) || 90,
          location: location.trim(),
          fee: feeInPence,
        };
        await updateDoc(doc(db, "clubs", club.id, "trainingSeries", params.seriesId), updates);

        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
        const trainingsSnap = await getDocs(
          query(collection(db, "clubs", club.id, "trainings"), where("seriesId", "==", params.seriesId))
        );
        const futureUpdates = trainingsSnap.docs
          .filter((d) => (d.data().date || "") >= todayStr)
          .map((d) => updateDoc(d.ref, updates));
        await Promise.all(futureUpdates);

        await alert({ title: "Updated", emoji: "✅", message: `${futureUpdates.length} future sessions updated.` });
      }
      router.back();
    } catch (err: any) {
      await alert({ title: "Error", message: err?.message || "Failed to update." });
    } finally {
      setSaving(false);
    }
  };

  if (loadingSeries) return <View style={{ flex: 1, backgroundColor: colors.bg, alignItems: "center", justifyContent: "center" }}><ActivityIndicator color={colors.brand} /></View>;

  return (
    <KeyboardAwareScroll style={{ flex: 1, backgroundColor: colors.bg }}>
      <ContentWidth>
        <View style={{ padding: 20 }}>
          <Label>Title</Label>
          <Input value={title} onChangeText={setTitle} placeholder="e.g. Winter Nets" />

          <Label>Day of week</Label>
          <View style={{ flexDirection: "row", gap: 6, flexWrap: "wrap" }}>
            {DAYS.map((d, i) => {
              const active = dayOfWeek === i;
              return (
                <Pressable
                  key={i}
                  onPress={() => setDayOfWeek(i)}
                  style={({ pressed }) => ({
                    paddingHorizontal: 14, paddingVertical: 10, borderRadius: radius.md,
                    borderWidth: 1.5, borderColor: active ? colors.brand : colors.line,
                    backgroundColor: active ? colors.brand50 : colors.white,
                    opacity: pressed ? 0.7 : 1,
                  })}
                >
                  <Text style={{ fontSize: 13, fontWeight: "700", color: active ? colors.brandDark : colors.inkMuted }}>{d}</Text>
                </Pressable>
              );
            })}
          </View>

          <View style={{ flexDirection: "row", gap: 12 }}>
            <View style={{ flex: 1 }}>
              <Label>Time</Label>
              <TimeInput value={time} onChange={setTime} />
            </View>
            <View style={{ flex: 1 }}>
              <Label>Duration (min)</Label>
              <Input value={duration} onChangeText={setDuration} placeholder="90" keyboardType="number-pad" />
            </View>
          </View>

          <Label>Location</Label>
          <Input value={location} onChangeText={setLocation} placeholder="e.g. Bushfield Indoor" />

          <Label>Start date</Label>
          <DateInput value={startDate} onChange={setStartDate} />

          <Label>End date</Label>
          <DateInput value={endDate} onChange={setEndDate} />

          <Label>Fee per session (£, 0 for free)</Label>
          <Input value={fee} onChangeText={setFee} placeholder="0" keyboardType="decimal-pad" />

          {needsRecreate && (
            <View style={{ backgroundColor: colors.amber50, borderRadius: radius.md, padding: 12, marginTop: 16, borderWidth: 1, borderColor: colors.amber200 }}>
              <Text style={{ fontSize: 12, color: colors.amber, fontWeight: "600" }}>
                Changing the day or dates will delete future sessions and recreate them. Past sessions are preserved.
              </Text>
            </View>
          )}

          <Pressable
            onPress={handleSave}
            disabled={saving}
            style={({ pressed }) => ({
              backgroundColor: colors.brand, borderRadius: radius.lg, padding: 16,
              alignItems: "center", marginTop: 24, opacity: pressed || saving ? 0.8 : 1,
            })}
          >
            {saving ? <ActivityIndicator color="#fff" /> : <Text style={{ color: "#fff", fontSize: 16, fontWeight: "700" }}>Save changes</Text>}
          </Pressable>
        </View>
        <View style={{ height: 40 }} />
      </ContentWidth>
      <AlertSheet {...alertProps} />
    </KeyboardAwareScroll>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <Text style={{ fontSize: 12, fontWeight: "800", color: colors.inkMuted, textTransform: "uppercase", letterSpacing: 1, marginTop: 16, marginBottom: 8 }}>
      {children}
    </Text>
  );
}

function Input(props: any) {
  return (
    <TextInput
      placeholderTextColor={colors.inkMuted}
      {...props}
      style={[{
        backgroundColor: colors.white, borderRadius: radius.md, borderWidth: 1, borderColor: colors.line,
        paddingHorizontal: 14, paddingVertical: 12, fontSize: 15, color: colors.ink,
      }, props.style]}
    />
  );
}
